import { Seo } from '../components/Seo';
import Logo from '../components/Logo';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff, Lock, BellOff, Sparkles, FileText, CheckCircle2 } from 'lucide-react';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { captureSignupViewed, captureSignupStarted, captureSignupCompleted } from '../lib/analytics';

const PROOF_POINTS = [
  {
    icon: Sparkles,
    title: '5 AI analyses, free',
    body: 'See the forensic detail on your own things first — exact model, condition and Australian replacement cost. No credit card to start.',
  },
  {
    icon: Lock,
    title: 'Your photos stay in your Drive',
    body: 'We create a Verifaeye_Vault folder in your own Google Drive. Your high-resolution images live on your cloud, not ours.',
  },
  {
    icon: FileText,
    title: 'Free insurer cover letter',
    body: 'When you need to claim, generate a supporting letter to send your insurer with your inventory — creating, exporting and emailing it is free.',
  },
  {
    icon: BellOff,
    title: 'No marketing spam',
    body: "We email you when a batch of analyses is ready or a duplicate is spotted. That's it, and you can switch it off.",
  },
];

const ProofPoints = ({ tone = 'dark' }) => {
  const dark = tone === 'dark';
  return (
    <ul className="space-y-6">
      {PROOF_POINTS.map(({ icon: Icon, title, body }) => (
        <li key={title} className="flex gap-4">
          <span
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
              dark ? 'bg-white/10 ring-1 ring-white/15' : 'bg-blue-50 ring-1 ring-blue-100'
            }`}
          >
            <Icon className={`h-4 w-4 ${dark ? 'text-blue-300' : 'text-blue-600'}`} />
          </span>
          <div>
            <p className={`text-base font-semibold ${dark ? 'text-white' : 'text-slate-900'}`}>{title}</p>
            <p className={`mt-1 text-sm leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{body}</p>
          </div>
        </li>
      ))}
    </ul>
  );
};

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { captureSignupViewed(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Please use at least 6 characters for your password');
      return;
    }

    setLoading(true);
    captureSignupStarted('email');

    try {
      await register(name, email, password);
      captureSignupCompleted('email');
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        detail === 'Email already registered'
          ? 'That email already has an account — sign in instead, or reset your password.'
          : detail || 'We could not create your account just now. Please check your details and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[1.05fr_1fr]">
      <Seo
        title="Create Your Free Account | VerifAEye"
        description="Create a free VerifAEye account and get 5 AI photo analyses. Document your household contents with EXIF-backed proof stored securely in your own Google Drive."
        path="/register"
      />

      {/* Proof panel — desktop */}
      <aside
        className="relative hidden overflow-hidden bg-slate-900 px-10 py-14 lg:flex lg:flex-col xl:px-16"
        data-testid="register-proof-panel"
      >
        <div
          className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-blue-600/20 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative animate-slide-up">
          <Link to="/" className="inline-flex items-center gap-2">
            <Logo size="lg" tone="dark" withTagline />
          </Link>

          <p className="mt-12 text-sm font-medium uppercase tracking-[0.18em] text-blue-400">
            The proof is in the detail
          </p>
          <h2
            className="mt-4 text-3xl font-bold leading-tight tracking-tight text-white xl:text-4xl"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Most people find out their cover was a best guess
            <span className="text-blue-400"> after</span> the disaster.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
            Photograph your contents now and VerifAEye reads the detail an assessor would — model, condition,
            replacement cost — and locks it to the date and place the photo was taken.
          </p>

          <video
            src="/videos/proof.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="mt-9 w-full max-w-lg rounded-2xl bg-slate-950 shadow-2xl ring-1 ring-white/10"
            data-testid="register-proof-video"
          />

          <div className="mt-10 max-w-lg">
            <ProofPoints tone="dark" />
          </div>

          <p className="mt-10 text-xs text-slate-500">
            Rebuilding an inventory after a loss is a far more complex job — and costs more.{' '}
            <Link to="/pricing" className="text-slate-300 underline hover:text-white" data-testid="register-pricing-link">
              See pricing
            </Link>
          </p>
        </div>
      </aside>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-5 py-10 sm:px-8 lg:px-12 xl:px-20">
        <div className="mx-auto w-full max-w-md animate-slide-up">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 lg:hidden">
            <Logo size="md" />
          </Link>

          <div className="flex flex-wrap gap-2" data-testid="register-trust-badges">
            {['5 free analyses', 'No credit card', 'Stored in your Google Drive'].map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                {badge}
              </span>
            ))}
          </div>

          <h1
            className="mt-6 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Create your free account
          </h1>
          <p className="mt-3 text-base text-slate-600">
            Takes under a minute. Your first five AI analyses are on us — enough to see exactly what
            VerifAEye finds in your own photos.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            {error && (
              <div
                className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                data-testid="register-error"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                data-testid="register-name-input"
                className="h-11 border-slate-200 focus:border-slate-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="register-email-input"
                className="h-11 border-slate-200 focus:border-slate-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="register-password-input"
                  className="h-11 border-slate-200 pr-10 focus:border-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="h-12 w-full bg-slate-900 text-base font-semibold text-white transition-colors hover:bg-slate-800"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Start free — 5 analyses included'
              )}
            </Button>

            <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-500">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span>
                Your photos stay in your own Google Drive, and we never sell or share your data. By creating an
                account you agree to our{' '}
                <Link to="/terms" className="text-teal-600 underline hover:text-teal-700" data-testid="register-terms-link">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-teal-600 underline hover:text-teal-700">
                  Privacy Policy
                </Link>
                .
              </span>
            </p>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton label="Sign up with Google" />
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-700"
              data-testid="register-login-link"
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Proof — mobile, kept below the form so the fields stay above the fold */}
        <section
          className="mx-auto mt-14 w-full max-w-md border-t border-slate-200 pt-10 lg:hidden"
          data-testid="register-proof-mobile"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
            The proof is in the detail
          </p>
          <h2
            className="mt-3 text-2xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            What you get before you pay a cent
          </h2>
          <div className="mt-8">
            <ProofPoints tone="light" />
          </div>
          <p className="mt-8 text-xs text-slate-500">
            Rebuilding an inventory after a loss is a far more complex job — and costs more.{' '}
            <Link to="/pricing" className="text-blue-600 underline hover:text-blue-700">
              See pricing
            </Link>
          </p>
        </section>
      </div>
    </div>
  );
};

export default Register;
